import React from 'react';
import { useGetPlayerGamesQuery } from '../../../services/api/sportsApi';
import ChartContainer from './ChartContainer';
import { useGetUserSessionQuery } from '../../../services/api/userApi';



// NFL Player Stats Component
const NflPlayerStats = ({ playerData, onBack, playerGames, isLoading, isError, isSubActive }) => {
    const { player, stats, teamLabel } = playerData;
    
    // Determine position type for stat display
    const isQB = player.Position === 'QB';
    const isRB = ['RB', 'FB'].includes(player.Position);
    const isReceiver = ['LWR', 'RWR', 'SWR', 'TE'].includes(player.Position);

    const formatStat = (value, decimals = 1) => {
        return value !== null && value !== undefined ? Number(value).toFixed(decimals) : 'N/A';
    };

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-6">
            {/* Header with Back Button */}
            <div className="flex items-center mb-6">
                {onBack && (
                    <button
                        onClick={onBack}
                        className="mr-4 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Back to lineup"
                    >
                        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                )}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{player.Name}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{player.Position} • {teamLabel}</p>
                </div>
            </div>
            {/* Chart */}
            <div className="mb-6">
                <ChartContainer 
                    playerGames={playerGames}
                    sport="nfl"
                    position={player.Position}
                    isLoading={isLoading}
                    isError={isError}
                    isSubActive={isSubActive}
                />
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {isQB && stats && (
                    <>
                        <StatCard label="Pass Yards" value={formatStat(stats.PassingYards, 0)} />
                        <StatCard label="Pass Yds/Game" value={formatStat(stats.PassingYardsPerGame)} />
                        <StatCard label="Completions" value={formatStat(stats.PassingCompletions, 0)} />
                        <StatCard label="Attempts" value={formatStat(stats.PassingAttempts, 0)} />
                        <StatCard label="Completion %" value={formatStat(stats.PassingCompletionPercentage)} />
                        <StatCard label="Pass TDs" value={formatStat(stats.PassingTouchdowns, 0)} />
                        <StatCard label="Pass TD/Game" value={formatStat(stats.PassingTouchdownsPerGame)} />
                        <StatCard label="Interceptions" value={formatStat(stats.PassingInterceptions, 0)} />
                        <StatCard label="Passer Rating" value={formatStat(stats.PassingRating)} />
                    </>
                )}

                {isRB && stats && (
                    <>
                        <StatCard label="Rush Yards" value={formatStat(stats.RushingYards, 0)} />
                        <StatCard label="Rush Yds/Game" value={formatStat(stats.RushingYardsPerGame)} />
                        <StatCard label="Rush Attempts" value={formatStat(stats.RushingAttempts, 0)} />
                        <StatCard label="Yds/Attempt" value={formatStat(stats.RushingYardsPerAttempt)} />
                        <StatCard label="Rush TDs" value={formatStat(stats.RushingTouchdowns, 0)} />
                        <StatCard label="Receptions" value={formatStat(stats.Receptions, 0)} />
                        <StatCard label="Rec Yards" value={formatStat(stats.ReceivingYards, 0)} />
                        <StatCard label="Rec TDs" value={formatStat(stats.ReceivingTouchdowns, 0)} />
                        <StatCard label="Total TDs" value={formatStat((stats.RushingTouchdowns || 0) + (stats.ReceivingTouchdowns || 0), 0)} />
                    </>
                )}

                {isReceiver && stats && (
                    <>
                        <StatCard label="Receptions" value={formatStat(stats.Receptions, 0)} />
                        <StatCard label="Targets" value={formatStat(stats.ReceivingTargets, 0)} />
                        <StatCard label="Rec Yards" value={formatStat(stats.ReceivingYards, 0)} />
                        <StatCard label="Rec Yds/Game" value={formatStat(stats.ReceivingYardsPerGame)} />
                        <StatCard label="Yds/Reception" value={formatStat(stats.ReceivingYardsPerReception)} />
                        <StatCard label="Rec TDs" value={formatStat(stats.ReceivingTouchdowns, 0)} />
                        <StatCard label="Rec TD/Game" value={formatStat(stats.ReceivingTouchdownsPerGame)} />
                        <StatCard label="Longest Rec" value={formatStat(stats.ReceivingLong, 0)} />
                    </>
                )}

                {!stats && (
                    <div className="col-span-2 md:col-span-3 text-center py-8 text-gray-500 dark:text-gray-400">
                        No stats available for this player
                    </div>
                )}
            </div>
        </div>
    );
};

// MLB Player Stats Component
const MlbPlayerStats = ({ playerData, onBack, playerGames, isLoading, isError, isSubActive }) => {
    const { player, stats, teamLabel, playerType } = playerData;
    const isPitcher = playerType === 'pitcher';

    const formatStat = (value, decimals = 2) => {
        return value !== null && value !== undefined ? Number(value).toFixed(decimals) : 'N/A';
    };

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-6">
            {/* Header with Back Button */}
            <div className="flex items-center mb-6">
                {onBack && (
                    <button
                        onClick={onBack}
                        className="mr-4 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Back to lineup"
                    >
                        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                )}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {player.FirstName} {player.LastName}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {player.Position} • {teamLabel}
                        {!isPitcher && player.BattingOrder && ` • Batting ${player.BattingOrder}`}
                    </p>
                </div>
            </div>

            {/* Chart */}
            <div className="mb-6">
                <ChartContainer 
                    playerGames={playerGames}
                    sport="mlb"
                    playerType={playerType}
                    isLoading={isLoading}
                    isError={isError}
                    isSubActive={isSubActive}
                />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {isPitcher && stats && (
                    <>
                        <StatCard label="Record (W-L)" value={`${stats.Wins || 0}-${stats.Losses || 0}`} />
                        <StatCard label="ERA" value={formatStat(stats.EarnedRunAverage)} />
                        <StatCard label="Games" value={formatStat(stats.Games, 0)} />
                        <StatCard label="Innings Pitched" value={formatStat(stats.InningsPitchedDecimal, 1)} />
                        <StatCard label="K / Game" value={formatStat(stats.PitchingStrikeoutsPerGame, 1)} />
                        <StatCard label="WHIP" value={formatStat(stats.WalksHitsPerInningsPitched, 3)} />
                        <StatCard label="K/9" value={formatStat(stats.PitchingStrikeoutsPerNineInnings, 1)} />
                        <StatCard label="BB/9" value={formatStat(stats.PitchingWalksPerNineInnings, 1)} />
                        <StatCard label="Opp BA" value={formatStat(stats.PitchingBattingAverageAgainst, 3)} />
                    </>
                )}

                {!isPitcher && stats && (
                    <>
                        <StatCard label="BA" value={formatStat(stats.BattingAverage, 3)} />
                        <StatCard label="Games" value={formatStat(stats.Games, 0)} />
                        <StatCard label="At Bats" value={formatStat(stats.AtBats, 0)} />
                        <StatCard label="Runs" value={formatStat(stats.Runs, 0)} />
                        <StatCard label="Hits" value={formatStat(stats.Hits, 0)} />
                        <StatCard label="Doubles" value={formatStat(stats.Doubles, 0)} />
                        <StatCard label="Triples" value={formatStat(stats.Triples, 0)} />
                        <StatCard label="Home Runs" value={formatStat(stats.HomeRuns, 0)} />
                        <StatCard label="RBIs" value={formatStat(stats.RunsBattedIn, 0)} />
                        <StatCard label="wOBA" value={formatStat(stats.WeightedOnBasePercentage, 3)} />
                        <StatCard label="OPS" value={formatStat(stats.OnBasePlusSlugging, 3)} />
                        <StatCard label="SLG" value={formatStat(stats.SluggingPercentage, 3)} />
                    </>
                )}

                {!stats && (
                    <div className="col-span-2 md:col-span-3 text-center py-8 text-gray-500 dark:text-gray-400">
                        No stats available for this player
                    </div>
                )}
            </div>


        </div>
    );
};

// NBA Player Stats Component
const NbaPlayerStats = ({ playerData, onBack, playerGames, isLoading, isError, isSubActive }) => {
    const { player, stats, teamLabel } = playerData;

    const formatStat = (value, decimals = 1) => {
        return value !== null && value !== undefined ? Number(value).toFixed(decimals) : 'N/A';
    };

    const formatPercentage = (value) => {
        if (value === null || value === undefined) return 'N/A';
        return `${(Number(value) * 100).toFixed(1)}%`;
    };

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-6">
            {/* Header with Back Button */}
            <div className="flex items-center mb-6">
                {onBack && (
                    <button
                        onClick={onBack}
                        className="mr-4 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Back to lineup"
                    >
                        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                )}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {player.FirstName} {player.LastName}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{player.Position} • {teamLabel}</p>
                </div>
            </div>

            {/* Chart */}
           <div className="mb-6">
                <ChartContainer 
                    playerGames={playerGames}
                    sport="nba"
                    isLoading={isLoading}
                    isError={isError}
                    isSubActive={isSubActive}
                />
            </div>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {stats && (
                    <>
                        <StatCard label="PPG" value={formatStat(stats.PointsPerGame)} />
                        <StatCard label="MPG" value={formatStat(stats.MinutesPerGame)} />
                        <StatCard label="Games" value={formatStat(stats.Games, 0)} />
                        <StatCard label="FG%" value={stats.FieldGoalsPercentage} />
                        <StatCard label="3P%" value={stats.ThreePointersPercentage} />
                        <StatCard label="FT%" value={stats.FreeThrowsPercentage} />
                        <StatCard label="Rebounds" value={formatStat(stats.ReboundsPerGame)} />
                        <StatCard label="Assists" value={formatStat(stats.AssistsPerGame)} />
                        <StatCard label="Steals" value={formatStat(stats.StealsPerGame)} />
                        <StatCard label="Blocks" value={formatStat(stats.BlocksPerGame)} />
                        <StatCard label="Turnovers" value={formatStat(stats.TurnoversPerGame)} />
                        <StatCard label="eFG%" value={formatStat(stats.EffectiveFieldGoalsPercentage, 3)} />
                    </>
                )}

                {!stats && (
                    <div className="col-span-2 md:col-span-3 text-center py-8 text-gray-500 dark:text-gray-400">
                        No stats available for this player
                    </div>
                )}
            </div>

        </div>
    );
};

// Reusable Stat Card Component
const StatCard = ({ label, value }) => (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</div>
        <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">{value}</div>
    </div>
);

// Main PlayerStats Component
const PlayerStats = ({ sport, playerData, onBack}) => {
    const { data: userSessionData, isLoading: isUserSessionLoading, isError: isUserSessionError, isFetching: isUserSessionFetching } = useGetUserSessionQuery();
    const isSubActive = userSessionData?.isSubActive?.isActive;
    const { data: playerGames, isLoading: playerGamesLoading, isError: playerGamesError } = useGetPlayerGamesQuery(
        { sport, num_games: 10, player_id: playerData.player.PlayerID, type: playerData?.playerType },
        { skip: !playerData?.player?.PlayerID || !sport }
    );

    if (!playerData) {
        return (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No player data available
            </div>
        );
    }

    // Render sport-specific component
    if (sport === 'nfl') {
        return (
            <NflPlayerStats 
                playerData={playerData} 
                onBack={onBack}
                playerGames={playerGames}
                isLoading={playerGamesLoading}
                isError={playerGamesError}
                isSubActive={isSubActive}
            />
        );
    }
    
    if (sport === 'mlb') {
        return (
            <MlbPlayerStats 
                playerData={playerData} 
                onBack={onBack}
                playerGames={playerGames}
                isLoading={playerGamesLoading}
                isError={playerGamesError}
                isSubActive={isSubActive}
            />
        );
    }
    
    if (sport === 'nba') {
        return (
            <NbaPlayerStats 
                playerData={playerData} 
                onBack={onBack}
                playerGames={playerGames}
                isLoading={playerGamesLoading}
                isError={playerGamesError}
                isSubActive={isSubActive}
            />
        );
    }

    return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Sport not supported
        </div>
    );
};

export default PlayerStats;